import { BACKEND_URL } from '../utils/config';
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { campaignAPI, aiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import LocationPicker from '../components/ui/LocationPicker';
import {
  Heart,
  GraduationCap,
  AlertTriangle,
  Building2,
  Trophy,
  Microscope,
  Rocket,
  Users,
  ArrowLeft,
  ArrowRight,
  Upload,
  Image as ImageIcon,
  Video,
  Check,
  Info,
  Target,
  Calendar,
  User,
  Shield,
  Eye,
  Sparkles,
  ChevronRight,
  Loader2,
  Wand2,
  AlertCircle,
  Save,
  CheckCircle,
  Search,
  X,
} from 'lucide-react';
import { categories, projectCategories, currencies } from '../data/mock';
import ImageGeneratorModal from '../components/ai/ImageGeneratorModal';

const iconMap = {
  Heart,
  GraduationCap,
  AlertTriangle,
  Building2,
  Trophy,
  Microscope,
  Rocket,
  Users,
};

const CreateCampaignPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  // Get initial step from URL or default to 1
  const getInitialStep = () => {
    const stepParam = searchParams.get('step');
    const step = parseInt(stepParam);
    return (step >= 1 && step <= 5) ? step : 1;
  };
  
  const [currentStep, setCurrentStep] = useState(getInitialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const totalSteps = 5;

  // Sync step with URL for browser back/forward navigation
  const updateStepInUrl = useCallback((step) => {
    // Use navigate with replace=false to add to history stack
    const newParams = new URLSearchParams(window.location.search);
    newParams.set('step', step.toString());
    navigate(`/create?${newParams.toString()}`, { replace: false });
  }, [navigate]);

  // Listen for browser back/forward button
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const step = parseInt(stepParam);
    if (step >= 1 && step <= 5 && step !== currentStep) {
      setCurrentStep(step);
    }
  }, [searchParams]);

  // Initialize step in URL if not present
  useEffect(() => {
    if (!searchParams.get('step')) {
      const newParams = new URLSearchParams(window.location.search);
      newParams.set('step', '1');
      navigate(`/create?${newParams.toString()}`, { replace: true });
    }
  }, []);

  // Initialize form data - check for saved data first
  const getInitialFormData = () => {
    const savedData = localStorage.getItem('pendingCampaign');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error('Failed to parse saved campaign data');
      }
    }
    // Check URL for type parameter
    const urlParams = new URLSearchParams(window.location.search);
    const typeFromUrl = urlParams.get('type') || '';
    
    return {
      // Step 1 - Type and Category
      type: typeFromUrl, // 'campaign' or 'project' - empty by default
      category: '',
      subcategory: '',
      // Step 2 - Basic Info
      title: '',
      goal: '',
      goalDisplay: '', // Formatted goal with commas for display
      currency: 'USD',
      duration: '30',
      location: '',
      country: '',
      videoFile: null,
      // Step 3 - Story
      story: '',
      coverImage: null,
      additionalImages: [],
      videoUrl: '',
      // Step 4 - Beneficiary
      beneficiaryType: '',
      beneficiaryName: '',
      beneficiaryRelation: '',
      // Step 5 - Settings
      enableEscrow: true,
      enableMilestones: true,
      milestones: [{ name: '', amount: '', description: '' }],
      allowAnonymousDonations: true,
      showDonorNames: true,
      agreeToTerms: false,
      agreeToPolicy: false,
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [currencySearch, setCurrencySearch] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);

  // Auto-advance to step 2 if type is set from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeFromUrl = urlParams.get('type');
    if (typeFromUrl && (typeFromUrl === 'campaign' || typeFromUrl === 'project') && currentStep === 1) {
      setCurrentStep(2);
      updateStepInUrl(2);
    }
  }, [currentStep]);

  // Filter currencies based on search
  const filteredCurrencies = currencies.filter(c => 
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currencySearch.toLowerCase())
  );

  // Real-time field validation on blur
  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'category':
        if (!value) error = 'Category is required';
        break;
      case 'subcategory':
        if (!value) error = 'Subcategory is required';
        break;
      case 'title':
        if (!value?.trim()) error = 'Title is required';
        break;
      case 'goal':
        if (!value || parseFloat(value) <= 0) error = 'Please enter a valid goal amount';
        break;
      case 'location':
        if (!value) error = 'Location is required';
        break;
      case 'story':
        if (!value?.trim()) error = 'Story is required';
        else if (value.length < 100) error = `Story should be at least 100 characters (${value.length}/100)`;
        break;
      case 'beneficiaryType':
        if (!value) error = 'Please select who will receive the funds';
        break;
      case 'beneficiaryName':
        if (formData.beneficiaryType === 'other' && !value?.trim()) error = 'Beneficiary name is required';
        break;
      default:
        break;
    }
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    return error;
  };

  // Handle field blur for immediate validation
  const handleFieldBlur = (field) => {
    validateField(field, formData[field]);
  };

  // Validate fields for current step - show only FIRST missing field error
  const validateStep = (step) => {
    const errors = {};
    
    if (step === 2) {
      // Validate fields in order - stop at first error
      if (!formData.category) {
        errors.category = 'Please select a category first';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.subcategory) {
        errors.subcategory = 'Please select a subcategory';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.title.trim()) {
        errors.title = 'Please enter a campaign title';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.goal || parseFloat(formData.goal) <= 0) {
        errors.goal = 'Please enter a valid goal amount';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.location) {
        errors.location = 'Please enter your location';
        setFieldErrors(errors);
        return false;
      }
    }
    
    if (step === 3) {
      if (!formData.story.trim()) {
        errors.story = 'Please tell your story';
        setFieldErrors(errors);
        return false;
      }
      if (formData.story.length < 100) {
        errors.story = 'Story should be at least 100 characters';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.coverImage) {
        errors.coverImage = 'Please upload a cover image for your campaign';
        setFieldErrors(errors);
        return false;
      }
    }
    
    if (step === 4) {
      if (!formData.beneficiaryType) {
        errors.beneficiaryType = 'Please select who will receive the funds';
        setFieldErrors(errors);
        return false;
      }
      if (formData.beneficiaryType === 'other' && !formData.beneficiaryName.trim()) {
        errors.beneficiaryName = 'Please enter the beneficiary name';
        setFieldErrors(errors);
        return false;
      }
    }
    
    if (step === 6) {
      if (!formData.agreeToTerms) {
        errors.agreeToTerms = 'You must agree to the terms';
        setFieldErrors(errors);
        return false;
      }
      if (!formData.agreeToPolicy) {
        errors.agreeToPolicy = 'You must agree to the privacy policy';
        setFieldErrors(errors);
        return false;
      }
    }
    
    setFieldErrors({});
    return true;
  };

  // Check if current step can proceed
  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!formData.type;
      case 2: return !!formData.category && !!formData.subcategory && !!formData.title?.trim() && !!formData.goal && parseFloat(formData.goal) > 0 && !!formData.location;
      case 3: return formData.story.trim() && formData.story.length >= 100 && !!formData.coverImage;
      case 4: return formData.beneficiaryType && (formData.beneficiaryType === 'self' || formData.beneficiaryName.trim());
      case 5: return formData.agreeToTerms && formData.agreeToPolicy;
      default: return true;
    }
  };

  // Check if we have pending campaign data and restore it (ONLY for logged-in users)
  useEffect(() => {
    if (!user) return; // Only restore for logged-in users
    
    const savedData = localStorage.getItem('pendingCampaign');
    const savedStep = localStorage.getItem('pendingCampaignStep');
    const pendingDraftSave = localStorage.getItem('pendingDraftSave');
    const pendingCampaignSubmit = localStorage.getItem('pendingCampaignSubmit');
    
    if (savedData) {
      try {
        // Restore the form data
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        
        // Restore the step they were on
        if (savedStep) {
          const step = parseInt(savedStep);
          setCurrentStep(step);
          updateStepInUrl(step);
        }
        
        // If user clicked "Submit" before logging in, auto-submit now
        if (pendingCampaignSubmit === 'true' && isAuthenticated) {
          // Clear the pending flag first
          localStorage.removeItem('pendingCampaignSubmit');
          
          // Auto-submit the campaign to backend
          const autoSubmitCampaign = async () => {
            setIsSubmitting(true);
            setError('');
            try {
              const milestones = parsedData.enableMilestones 
                ? (parsedData.milestones?.filter(m => m.name && m.amount).map(m => ({
                    name: m.name,
                    amount: parseFloat(m.amount),
                    description: m.description || '',
                  })) || [])
                : [];
              
              const campaignData = {
                title: parsedData.title,
                story: parsedData.story || '',
                goal: parseFloat(parsedData.goal),
                category: parsedData.category,
                subcategory: parsedData.subcategory || '',
                location: parsedData.location || '',
                country: parsedData.country || '',
                currency: parsedData.currency || 'USD',
                duration: parsedData.duration === 'ongoing' ? 365 : parseInt(parsedData.duration) || 30,
                beneficiary_type: parsedData.beneficiaryType || 'self',
                beneficiary_name: parsedData.beneficiaryName || '',
                beneficiary_relation: parsedData.beneficiaryRelation || '',
                image: parsedData.coverImage || '',
                // Use uploaded video file URL if available, otherwise use pasted URL
                video_url: parsedData.videoFile || parsedData.videoUrl || '',
                enable_escrow: parsedData.enableEscrow || false,
                enable_milestones: parsedData.enableMilestones || false,
                milestones: milestones,
                type: parsedData.type || 'campaign',
              };

              const response = await campaignAPI.create(campaignData);
              
              // Clear localStorage since campaign is submitted
              localStorage.removeItem('pendingCampaign');
              localStorage.removeItem('pendingCampaignStep');
              
              // Navigate to the created campaign or verification page
              if (response.status === 'pending_verification') {
                navigate('/verification?campaign=' + response.id);
              } else if (response.id) {
                navigate(`/campaign/${response.id}`);
              } else {
                navigate('/dashboard');
              }
            } catch (err) {
              console.error('Failed to auto-submit campaign:', err);
              setError('Failed to submit campaign. Please review your details and try again.');
              // Keep data in localStorage as fallback
              setPendingSubmit(true);
            } finally {
              setIsSubmitting(false);
            }
          };
          
          // Delay slightly to ensure component is fully mounted
          setTimeout(autoSubmitCampaign, 500);
        }
        // If user clicked "Save Draft" before logging in, auto-save now
        else if (pendingDraftSave === 'true' && isAuthenticated) {
          // Clear the pending flag first
          localStorage.removeItem('pendingDraftSave');
          
          // Auto-save the draft to backend
          const autoSaveDraft = async () => {
            setSavingDraft(true);
            try {
              const campaignData = {
                title: parsedData.title || 'Untitled Draft',
                description: parsedData.story || '',
                category: parsedData.category || 'other',
                subcategory: parsedData.subcategory || '',
                goal: parseFloat(parsedData.goal) || 0,
                currency: parsedData.currency || 'USD',
                type: parsedData.type || 'campaign',
                location: parsedData.location || '',
                country: parsedData.country || '',
                beneficiary_type: parsedData.beneficiaryType || 'self',
                beneficiary_name: parsedData.beneficiaryName || '',
                enable_escrow: parsedData.enableEscrow,
                milestones: parsedData.milestones?.filter(m => m.name && m.amount) || [],
                image: parsedData.coverImage || '',
                // Include video URL (from file upload or pasted URL)
                video_url: parsedData.videoFile || parsedData.videoUrl || '',
                save_as_draft: true,
                draft_step: parseInt(savedStep) || currentStep,
              };

              await campaignAPI.create(campaignData);
              
              // Clear localStorage since it's saved to backend
              localStorage.removeItem('pendingCampaign');
              localStorage.removeItem('pendingCampaignStep');
              
              setDraftSaved(true);
              setPendingSubmit(true); // Show success message
              
              setTimeout(() => {
                setDraftSaved(false);
                navigate('/dashboard');
              }, 2000);
            } catch (err) {
              console.error('Failed to auto-save draft:', err);
              // Keep data in localStorage as fallback
              setDraftSaved(true);
              setTimeout(() => setDraftSaved(false), 3000);
            } finally {
              setSavingDraft(false);
            }
          };
          
          autoSaveDraft();
        } else if (isAuthenticated) {
          // Just show message that we restored their data
          setPendingSubmit(true);
        }
      } catch (e) {
        console.error('Failed to restore campaign data:', e);
      }
    }
  }, [isAuthenticated, user]);

  // Clear the pending submit flag after showing the message
  useEffect(() => {
    if (pendingSubmit && isAuthenticated) {
      // Keep the message visible for a moment, then clear
      const timer = setTimeout(() => {
        setPendingSubmit(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [pendingSubmit, isAuthenticated]);

  // Save draft function
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [categorySearch, setCategorySearch] = useState('');

  // Auto-save to localStorage whenever formData changes (ONLY for logged-in users)
  useEffect(() => {
    if (user && formData.type) { // Only auto-save if user is logged in and has started
      const autoSaveTimer = setTimeout(() => {
        localStorage.setItem('pendingCampaign', JSON.stringify(formData));
        localStorage.setItem('pendingCampaignStep', currentStep.toString());
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      }, 1000); // Debounce 1 second

      return () => clearTimeout(autoSaveTimer);
    }
  }, [formData, currentStep, user]);

  // Auto-save on step change (ONLY for logged-in users)
  useEffect(() => {
    if (user && currentStep > 1 && formData.type) {
      localStorage.setItem('pendingCampaign', JSON.stringify(formData));
      localStorage.setItem('pendingCampaignStep', currentStep.toString());
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    }
  }, [currentStep, user]);

  // Track if user manually saved draft to prevent duplicate saves on unmount
  const [manualDraftSaved, setManualDraftSaved] = useState(false);

  // Auto-save to backend when user navigates away (for logged-in users with data)
  // DISABLED: This was causing duplicate drafts. Manual save via saveDraft() is sufficient.
  // The localStorage auto-save on formData changes provides backup without creating duplicates.

  const saveDraft = async () => {
    if (!isAuthenticated) {
      // Save to localStorage for non-authenticated users
      localStorage.setItem('pendingCampaign', JSON.stringify(formData));
      localStorage.setItem('pendingCampaignStep', currentStep.toString());
      localStorage.setItem('pendingDraftSave', 'true'); // Flag to auto-save after login
      
      // Redirect to login page
      navigate('/auth?redirect=/create-campaign&action=save-draft');
      return;
    }

    setSavingDraft(true);
    try {
      // Save to backend as draft
      const campaignData = {
        title: formData.title || 'Untitled Draft',
        description: formData.story || '',
        category: formData.category || 'other',
        subcategory: formData.subcategory || '',
        goal: parseFloat(formData.goal) || 0,
        currency: formData.currency || 'USD',
        type: formData.type || 'campaign',
        location: formData.location || '',
        country: formData.country || '',
        beneficiary_type: formData.beneficiaryType || 'self',
        beneficiary_name: formData.beneficiaryName || '',
        enable_escrow: formData.enableEscrow,
        milestones: formData.milestones?.filter(m => m.name && m.amount) || [],
        save_as_draft: true,
        draft_step: currentStep,
      };

      await campaignAPI.create(campaignData);
      
      // Clear localStorage since it's saved to backend
      localStorage.removeItem('pendingCampaign');
      localStorage.removeItem('pendingCampaignStep');
      
      setDraftSaved(true);
      setTimeout(() => {
        setDraftSaved(false);
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Failed to save draft:', err);
      // Fallback to localStorage
      localStorage.setItem('pendingCampaign', JSON.stringify(formData));
      localStorage.setItem('pendingCampaignStep', currentStep.toString());
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitCampaign = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const milestones = formData.enableMilestones 
        ? formData.milestones.filter(m => m.name && m.amount).map(m => ({
            name: m.name,
            amount: parseFloat(m.amount),
            description: m.description || '',
          }))
        : [];

      const campaignData = {
        title: formData.title,
        story: formData.story,
        goal: parseFloat(formData.goal),
        category: formData.category,
        subcategory: formData.subcategory,
        location: formData.location,
        country: formData.country,
        currency: formData.currency,
        duration: formData.duration === 'ongoing' ? 365 : parseInt(formData.duration),
        beneficiary_type: formData.beneficiaryType,
        beneficiary_name: formData.beneficiaryName,
        beneficiary_relation: formData.beneficiaryRelation,
        image: formData.coverImage,
        // Use uploaded video file URL if available, otherwise use pasted URL
        video_url: formData.videoFile || formData.videoUrl || '',
        enable_escrow: formData.enableEscrow,
        enable_milestones: formData.enableMilestones,
        milestones: milestones,
      };

      const newCampaign = await campaignAPI.create(campaignData);
      
      // Clear saved data after successful creation
      localStorage.removeItem('pendingCampaign');
      localStorage.removeItem('pendingCampaignStep');
      setPendingSubmit(false);
      
      // Check if campaign is pending verification
      if (newCampaign.status === 'pending_verification') {
        // Redirect to verification page
        navigate('/verification?campaign=' + newCampaign.id);
      } else {
        // Campaign is active, go to campaign page
        navigate(`/campaign/${newCampaign.id}`);
      }
    } catch (err) {
      console.error('Failed to create campaign:', err);
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to create campaign. Please try again.';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      setError(errorMessage);
      setPendingSubmit(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (!validateStep(currentStep)) {
      return;
    }
    if (currentStep < totalSteps) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      updateStepInUrl(newStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      updateStepInUrl(newStep);
    } else {
      // On step 1, go back to home page
      navigate('/');
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      // Save campaign data before redirecting to login/register
      const dataToSave = { ...formData };
      
      // Only remove image if it's a base64 string (large), keep URL-based images
      if (dataToSave.coverImage && dataToSave.coverImage.startsWith('data:') && dataToSave.coverImage.length > 50000) {
        // Base64 image is too large, remove it
        dataToSave.coverImage = null;
        dataToSave.imageWasRemoved = true;
      }
      // If coverImage is a URL (starts with http), keep it as is
      
      try {
        localStorage.setItem('pendingCampaign', JSON.stringify(dataToSave));
        localStorage.setItem('pendingCampaignStep', currentStep.toString());
      } catch (e) {
        // If still fails, save without image
        console.error('localStorage error:', e);
        dataToSave.coverImage = null;
        dataToSave.imageWasRemoved = true;
        localStorage.setItem('pendingCampaign', JSON.stringify(dataToSave));
        localStorage.setItem('pendingCampaignStep', currentStep.toString());
      }
      navigate('/login?redirect=create&pending=campaign');
      return;
    }

    // Use the shared submit function
    await handleSubmitCampaign();
  };

  const addMilestone = () => {
    setFormData({
      ...formData,
      milestones: [...formData.milestones, { name: '', amount: '', description: '' }],
    });
  };

  const updateMilestone = (index, field, value) => {
    const newMilestones = [...formData.milestones];
    
    // Validate amount - reject negative values
    if (field === 'amount') {
      const numValue = parseFloat(value);
      if (value !== '' && (isNaN(numValue) || numValue < 0)) {
        // Don't update with negative values
        return;
      }
    }
    
    newMilestones[index][field] = value;
    setFormData({ ...formData, milestones: newMilestones });
  };

  // AI Story Generation
  const generateStory = async () => {
    if (!formData.title || !formData.goal || !formData.category) {
      setError('Please fill in the title, goal, and category first.');
      return;
    }

    setIsGeneratingStory(true);
    setError('');

    try {
      const result = await aiAPI.generateStory({
        category: formData.category,
        title: formData.title,
        goal: formData.goal,
        location: formData.location,
        existing_story: formData.story,
      });

      if (result.success && result.story) {
        updateFormData('story', result.story);
      } else {
        setError(result.error || 'Failed to generate story. Please try again.');
      }
    } catch (err) {
      console.error('Story generation error:', err);
      setError('Failed to generate story. Please try again.');
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Step 1: Type Selection (Campaign or Project)
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('whatWouldYouLikeToCreate')}</h2>
              <p className="text-muted-foreground">{t('chooseBetweenCampaign')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Campaign Card */}
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, type: 'campaign', category: '' }));
                  setTimeout(() => {
                    setCurrentStep(2);
                    updateStepInUrl(2);
                  }, 300);
                }}
                className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                  formData.type === 'campaign'
                    ? 'border-emerald-500 bg-emerald-500/10 shadow-lg scale-[1.02]'
                    : 'border-border hover:border-emerald-500/50 bg-card hover:bg-emerald-500/5'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${formData.type === 'campaign' ? 'bg-emerald-500/20' : 'bg-secondary'}`}>
                    <Heart className={`w-8 h-8 ${formData.type === 'campaign' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <h3 className={`font-bold text-xl ${formData.type === 'campaign' ? 'text-emerald-400' : 'text-foreground'}`}>
                      {t('campaign')}
                    </h3>
                    <p className="text-xs text-muted-foreground">{t('fundraisingForCauses')}</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  {t('campaignPerfectFor')}
                </p>
                
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-medium text-foreground">{t('bestFor')}</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {t('medicalBillsTreatments')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {t('emergencyDisaster')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {t('educationScholarships')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {t('memorialFamily')}
                    </li>
                  </ul>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{t('medical')}</span>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{t('emergency')}</span>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{t('education')}</span>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{t('community')}</span>
                </div>
              </button>
              
              {/* Creator Project Card */}
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, type: 'project', category: '' }));
                  setTimeout(() => {
                    setCurrentStep(2);
                    updateStepInUrl(2);
                  }, 300);
                }}
                className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                  formData.type === 'project'
                    ? 'border-teal-500 bg-teal-500/10 shadow-lg scale-[1.02]'
                    : 'border-border hover:border-teal-500/50 bg-card hover:bg-teal-500/5'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${formData.type === 'project' ? 'bg-teal-500/20' : 'bg-secondary'}`}>
                    <Rocket className={`w-8 h-8 ${formData.type === 'project' ? 'text-teal-400' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <h3 className={`font-bold text-xl ${formData.type === 'project' ? 'text-teal-400' : 'text-foreground'}`}>
                      {t('creatorProject')}
                    </h3>
                    <p className="text-xs text-muted-foreground">{t('buildCreateResearch')}</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  {t('projectPerfectFor')}
                </p>
                
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-medium text-foreground">{t('bestFor')}</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                      {t('techSoftware')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                      {t('scientificMedicalResearch')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                      {t('gamesFilmsCreative')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                      {t('artDesignMusic')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                      {t('inventionsStartups')}
                    </li>
                  </ul>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">Tech</span>
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">{t('research')}</span>
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">Games</span>
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">Art</span>
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">Music</span>
                </div>
              </button>
            </div>
          </div>
        );

      case 2:
        // Step 2: Basic Details (merged with Category/Subcategory)
        const availableCategories = formData.type === 'project' ? projectCategories : categories;
        const sortedCategories = [...availableCategories].sort((a, b) => a.name.localeCompare(b.name));
        const selectedCategoryObj = availableCategories.find(c => c.id === formData.category);
        const sortedSubcategories = selectedCategoryObj?.subcategories ? [...selectedCategoryObj.subcategories].sort() : [];
        
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">{formData.type === 'project' ? t('projectDetails') : t('campaignDetails')}</h2>
              <p className="text-muted-foreground">{t('setupBasicInfo')} {formData.type === 'project' ? t('project') : t('campaign')}</p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Category and Subcategory Dropdowns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category" className="text-base text-foreground">{t('category')} *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => {
                      updateFormData('category', value);
                      updateFormData('subcategory', ''); // Reset subcategory when category changes
                      setFieldErrors(prev => ({ ...prev, category: null, subcategory: null }));
                    }}
                    onOpenChange={(open) => {
                      if (!open && !formData.category) {
                        validateField('category', formData.category);
                      }
                    }}
                  >
                    <SelectTrigger className={`mt-2 py-6 ${fieldErrors.category ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder={t('selectCategory')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {sortedCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.category && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.category}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="subcategory" className="text-base text-foreground">{t('subcategory')} *</Label>
                  <Select 
                    value={formData.subcategory} 
                    onValueChange={(value) => {
                      updateFormData('subcategory', value);
                      setFieldErrors(prev => ({ ...prev, subcategory: null }));
                    }}
                    onOpenChange={(open) => {
                      if (!open && formData.category && !formData.subcategory) {
                        validateField('subcategory', formData.subcategory);
                      }
                    }}
                    disabled={!formData.category}
                  >
                    <SelectTrigger className={`mt-2 py-6 ${fieldErrors.subcategory ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder={formData.category ? t('selectSubcategory') : t('selectCategoryFirst')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {sortedSubcategories.map((sub) => (
                        <SelectItem key={sub} value={sub}>
                          {sub}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.subcategory && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.subcategory}
                    </p>
                  )}
                </div>
              </div>

              {/* Title - enabled only after subcategory is selected */}
              <div className={!formData.subcategory ? 'opacity-50 pointer-events-none' : ''}>
                <div className="flex items-center justify-between">
                  <Label htmlFor="title" className="text-base text-foreground">{formData.type === 'project' ? t('projectTitle') : t('campaignTitle')} *</Label>
                  <span className={`text-sm ${formData.title.split(' ').filter(w => w).length > 15 ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {formData.title.split(' ').filter(w => w).length}/15 words
                  </span>
                </div>
                <Input
                  id="title"
                  placeholder={formData.type === 'project' ? t('giveProjectTitle') : t('giveCampaignTitle')}
                  value={formData.title}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const wordCount = newValue.split(' ').filter(w => w).length;
                    // Allow input but warn if over limit
                    if (wordCount <= 15) {
                      updateFormData('title', newValue);
                      if (fieldErrors.title) setFieldErrors(prev => ({ ...prev, title: null }));
                    }
                  }}
                  onBlur={() => handleFieldBlur('title')}
                  disabled={!formData.subcategory}
                  className={`mt-2 py-6 text-lg bg-secondary ${fieldErrors.title ? 'border-red-500' : ''}`}
                />
                {fieldErrors.title ? (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {fieldErrors.title}
                  </p>
                ) : formData.title.split(' ').filter(w => w).length >= 13 ? (
                  <p className="text-sm text-amber-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {15 - formData.title.split(' ').filter(w => w).length} words remaining
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">{!formData.subcategory ? t('selectCategorySubcategoryFirst') : t('goodTitlesGetMore')}</p>
                )}
              </div>

              {/* Currency and Goal - Currency first, then Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currency" className="text-base">{t('currencyLabel')} *</Label>
                  <Select value={formData.currency} onValueChange={(value) => updateFormData('currency', value)}>
                    <SelectTrigger className="mt-2 py-6">
                      <SelectValue placeholder={t('selectCurrency')}>
                        {formData.currency && (
                          <span className="flex items-center gap-2">
                            <span>{currencies.find(c => c.code === formData.currency)?.flag}</span>
                            <span>{formData.currency} - {currencies.find(c => c.code === formData.currency)?.name}</span>
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <div className="px-2 py-1.5 sticky top-0 bg-card z-10">
                        <Input
                          placeholder={t('searchCurrency')}
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      {/* Popular currencies first */}
                      {currencySearch === '' && (
                        <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Popular Currencies</div>
                      )}
                      {filteredCurrencies.filter(c => currencySearch === '' ? c.popular : true).map((curr) => (
                        <SelectItem key={curr.code} value={curr.code}>
                          <span className="flex items-center gap-2">
                            <span>{curr.flag}</span>
                            <span>{curr.code} - {curr.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                      {/* All other currencies when not searching */}
                      {currencySearch === '' && (
                        <>
                          <div className="px-2 py-1 text-xs text-muted-foreground font-medium mt-2 border-t border-border pt-2">All Currencies</div>
                          {filteredCurrencies.filter(c => !c.popular).map((curr) => (
                            <SelectItem key={curr.code} value={curr.code}>
                              <span className="flex items-center gap-2">
                                <span>{curr.flag}</span>
                                <span>{curr.code} - {curr.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="goal" className="text-base">Fundraising Goal *</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                      {currencies.find(c => c.code === formData.currency)?.symbol || '$'}
                    </span>
                    <Input
                      id="goal"
                      type="text"
                      placeholder="10,000"
                      value={formData.goalDisplay || ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9.]/g, '');
                        const displayValue = rawValue ? Number(rawValue).toLocaleString('en-US') : '';
                        setFormData(prev => ({ ...prev, goal: rawValue, goalDisplay: displayValue }));
                        if (fieldErrors.goal) setFieldErrors(prev => ({ ...prev, goal: null }));
                      }}
                      onBlur={() => handleFieldBlur('goal')}
                      disabled={!formData.title.trim()}
                      className={`pl-8 py-6 text-lg ${fieldErrors.goal ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {fieldErrors.goal ? (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.goal}
                    </p>
                  ) : !formData.title.trim() && (
                    <p className="text-sm text-muted-foreground mt-1">Enter title first</p>
                  )}
                </div>
              </div>

              {/* Duration */}
              <div>
                <Label htmlFor="duration" className="text-base">{formData.type === 'project' ? 'Project Duration' : 'Campaign Duration'}</Label>
                <Select value={formData.duration} onValueChange={(value) => updateFormData('duration', value)}>
                  <SelectTrigger className="mt-2 py-6">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days (Recommended)</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days (3 months)</SelectItem>
                    <SelectItem value="120">120 days (4 months)</SelectItem>
                    <SelectItem value="150">150 days (5 months)</SelectItem>
                    <SelectItem value="180">180 days (6 months)</SelectItem>
                    <SelectItem value="ongoing">No end date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Location - enabled only after goal is filled */}
              <div className={!formData.goal || parseFloat(formData.goal) <= 0 ? 'opacity-50 pointer-events-none' : ''}>
                <Label className="text-base">Location *</Label>
                <p className="text-sm text-muted-foreground mb-2">{!formData.goal ? 'Enter goal amount first' : 'Enter your city or use current location'}</p>
                <LocationPicker
                  value={formData.location}
                  onChange={(locationData) => {
                    const locationValue = locationData?.display || locationData || '';
                    updateFormData('location', locationValue);
                    if (fieldErrors.location) setFieldErrors(prev => ({ ...prev, location: null }));
                  }}
                  placeholder="e.g., Cannes, France or London, UK"
                  className={fieldErrors.location ? '[&_input]:border-red-500' : ''}
                  disabled={!formData.goal || parseFloat(formData.goal) <= 0}
                />
                {fieldErrors.location && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {fieldErrors.location}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('tellYourStory')}</h2>
              <p className="text-muted-foreground">
                {formData.type === 'project' 
                  ? t('shareWhatBuilding')
                  : t('shareCauseMatters')}
              </p>
            </div>

            <div className="max-w-2xl mx-auto space-y-6">
              {/* Cover Image Upload */}
              <div>
                <Label className="text-base text-foreground">{t('coverImage')} *</Label>
                <input
                  type="file"
                  id="coverImageInput"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Validate file size (max 10MB - aligned with backend)
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      if (file.size > maxSize) {
                        setError('Image file too large. Maximum size is 10MB. Please compress your image or use a smaller file.');
                        e.target.value = ''; // Reset input
                        return;
                      }
                      
                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        setError('Please select a valid image file (JPG, PNG, GIF, WebP).');
                        e.target.value = '';
                        return;
                      }
                      
                      // Upload image immediately to server
                      const uploadImage = async () => {
                        try {
                          setError(null);
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          
                          const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
                            method: 'POST',
                            body: formDataUpload
                          });
                          
                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
                          }
                          
                          const result = await response.json();
                          if (result.success) {
                            // Store the full URL
                            const imageUrl = `${BACKEND_URL}${result.url}`;
                            updateFormData('coverImage', imageUrl);
                          } else {
                            throw new Error(result.detail || 'Upload failed');
                          }
                        } catch (err) {
                          console.error('Upload error:', err);
                          setError(err.message || 'Failed to upload image. Please try again with a smaller file.');
                        }
                      };
                      uploadImage();
                    }
                  }}
                />
                <div 
                  className="mt-2 border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-emerald-500/50 transition-colors cursor-pointer bg-secondary/50"
                  onClick={() => document.getElementById('coverImageInput').click()}
                >
                  {formData.coverImage ? (
                    <div className="relative">
                      <img 
                        src={formData.coverImage} 
                        alt="Cover preview" 
                        className="w-full h-48 object-cover rounded-lg mx-auto"
                      />
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateFormData('coverImage', null);
                        }}
                      >
                        {t('removeImage')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-2">{t('dragAndDropImage')}</p>
                      <p className="text-sm text-muted-foreground">{t('imageRequirements')}</p>
                      <Button variant="outline" className="mt-4" type="button">
                        <Upload className="w-4 h-4 mr-2" /> {t('uploadImage')}
                      </Button>
                    </>
                  )}
                </div>
                
                {/* AI Image Generator Button */}
                <div className="mt-3 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-teal-500/50 hover:bg-teal-500/10 text-teal-400"
                    onClick={() => setShowImageGenerator(true)}
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    {t('generateAIImage')}
                  </Button>
                </div>
                {fieldErrors.coverImage && (
                  <p className="text-sm text-red-500 flex items-center gap-1 mt-2">
                    <AlertCircle className="w-3 h-3" /> {fieldErrors.coverImage}
                  </p>
                )}
              </div>

              {/* Story */}
              <div>
                <Label htmlFor="story" className="text-base text-foreground">{t('yourStoryLabel')} *</Label>
                <Textarea
                  id="story"
                  placeholder={t('storyPlaceholder')}
                  value={formData.story}
                  onChange={(e) => {
                    updateFormData('story', e.target.value);
                    if (fieldErrors.story) setFieldErrors(prev => ({ ...prev, story: null }));
                  }}
                  onBlur={() => handleFieldBlur('story')}
                  className={`mt-2 min-h-[250px] bg-secondary ${fieldErrors.story ? 'border-red-500' : ''}`}
                />
                {/* Footer row with character count and AI button */}
                <div className="flex justify-between items-center mt-2">
                  {fieldErrors.story ? (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.story}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {formData.story.length} / 5000 {t('characters')} ({t('minCharacters')} 100)
                    </p>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border-teal-500/50 text-teal-400 hover:bg-teal-500/20 hover:text-purple-300"
                    onClick={generateStory}
                    disabled={isGeneratingStory}
                    type="button"
                  >
                    {isGeneratingStory ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" /> AI Write
                      </>
                    )}
                  </Button>
                </div>
                {error && currentStep === 4 && (
                  <p className="text-red-400 text-sm mt-1">{error}</p>
                )}
              </div>

              {/* Video Section */}
              <div>
                <Label className="text-base text-foreground">Video (Optional)</Label>
                <p className="text-sm text-muted-foreground mb-3">Add a video to increase engagement - campaigns with videos raise 150% more!</p>
                
                <div className="space-y-4">
                  {/* Video Upload */}
                  <div>
                    <input
                      type="file"
                      id="videoFileInput"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Check file size (max 100MB for 2 min video)
                          if (file.size > 100 * 1024 * 1024) {
                            setError('Video file too large. Maximum size is 100MB (2 minutes).');
                            return;
                          }
                          // Check video duration
                          const video = document.createElement('video');
                          video.preload = 'metadata';
                          video.onloadedmetadata = function() {
                            window.URL.revokeObjectURL(video.src);
                            if (video.duration > 120) {
                              setError('Video is too long. Maximum duration is 2 minutes.');
                              return;
                            }
                            // Upload video
                            const uploadVideo = async () => {
                              setVideoUploading(true);
                              setError('');
                              try {
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                
                                // Get auth token for authenticated upload
                                const token = localStorage.getItem('token');
                                
                                const response = await fetch(`${BACKEND_URL}/api/r2/upload/video`, {
                                  method: 'POST',
                                  body: formDataUpload,
                                  headers: token ? {
                                    'Authorization': `Bearer ${token}`
                                  } : {}
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  console.log('Video upload successful:', data);
                                  updateFormData('videoFile', data.url);
                                  updateFormData('videoUrl', ''); // Clear URL if file uploaded
                                } else {
                                  const errorData = await response.json().catch(() => ({}));
                                  console.error('Video upload failed:', response.status, errorData);
                                  throw new Error(errorData.detail || 'Upload failed');
                                }
                              } catch (err) {
                                console.error('Video upload error:', err);
                                setError('Failed to upload video. Please try again or use a YouTube link.');
                              } finally {
                                setVideoUploading(false);
                              }
                            };
                            uploadVideo();
                          };
                          video.src = URL.createObjectURL(file);
                        }
                      }}
                    />
                    <div 
                      className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-emerald-500/50 transition-colors cursor-pointer bg-secondary/50"
                      onClick={() => !videoUploading && document.getElementById('videoFileInput').click()}
                    >
                      {videoUploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-8 h-8 animate-spin text-emerald-400 mb-2" />
                          <p className="text-muted-foreground">Uploading video...</p>
                        </div>
                      ) : formData.videoFile ? (
                        <div className="flex flex-col items-center">
                          <Check className="w-8 h-8 text-emerald-400 mb-2" />
                          <p className="text-foreground font-medium">Video uploaded successfully!</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateFormData('videoFile', null);
                            }}
                          >
                            Remove Video
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Video className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground mb-1">Upload a video (max 2 minutes)</p>
                          <p className="text-xs text-muted-foreground">MP4, MOV, WebM up to 100MB</p>
                          <Button variant="outline" size="sm" className="mt-3" type="button">
                            <Upload className="w-4 h-4 mr-2" /> Upload Video
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-sm text-muted-foreground">OR</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  {/* YouTube/Video URL */}
                  <div>
                    <Label htmlFor="videoUrl" className="text-sm text-muted-foreground">Paste YouTube or Video URL</Label>
                    <div className="relative mt-2">
                      <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="videoUrl"
                        placeholder="https://youtube.com/watch?v=..."
                        value={formData.videoUrl}
                        onChange={(e) => {
                          updateFormData('videoUrl', e.target.value);
                          if (e.target.value) updateFormData('videoFile', null); // Clear file if URL entered
                        }}
                        disabled={!!formData.videoFile}
                        className="pl-10 bg-secondary border-border"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Who will receive the funds?</h2>
              <p className="text-muted-foreground">Tell us about the beneficiary of this {formData.type === 'project' ? 'project' : 'campaign'}</p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Beneficiary Type */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    updateFormData('beneficiaryType', 'self');
                    setFieldErrors(prev => ({ ...prev, beneficiaryType: null }));
                    // Auto-advance when selecting "Myself"
                    setTimeout(() => {
                      setCurrentStep(5);
                      updateStepInUrl(5);
                    }, 300);
                  }}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.beneficiaryType === 'self'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-border hover:border-emerald-500/50'
                  }`}
                >
                  <User className={`w-8 h-8 mb-3 ${formData.beneficiaryType === 'self' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                  <h3 className="font-semibold text-foreground">Myself</h3>
                  <p className="text-sm text-muted-foreground">I'm raising funds for myself</p>
                </button>
                <button
                  onClick={() => {
                    updateFormData('beneficiaryType', 'other');
                    setFieldErrors(prev => ({ ...prev, beneficiaryType: null }));
                  }}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.beneficiaryType === 'other'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-border hover:border-emerald-500/50'
                  }`}
                >
                  <Users className={`w-8 h-8 mb-3 ${formData.beneficiaryType === 'other' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                  <h3 className="font-semibold text-foreground">Someone Else</h3>
                  <p className="text-sm text-muted-foreground">I'm raising funds for another person or organization</p>
                </button>
              </div>

              {fieldErrors.beneficiaryType && (
                <p className="text-sm text-red-500 flex items-center gap-1 -mt-4">
                  <AlertCircle className="w-3 h-3" /> {fieldErrors.beneficiaryType}
                </p>
              )}

              {formData.beneficiaryType === 'other' && (
                <>
                  <div>
                    <Label htmlFor="beneficiaryName" className="text-base text-foreground">Beneficiary Name *</Label>
                    <Input
                      id="beneficiaryName"
                      placeholder="Full name of the person or organization"
                      value={formData.beneficiaryName}
                      onChange={(e) => {
                        updateFormData('beneficiaryName', e.target.value);
                        if (fieldErrors.beneficiaryName) setFieldErrors(prev => ({ ...prev, beneficiaryName: null }));
                      }}
                      onBlur={() => handleFieldBlur('beneficiaryName')}
                      className={`mt-2 py-6 bg-secondary ${fieldErrors.beneficiaryName ? 'border-red-500' : ''}`}
                    />
                    {fieldErrors.beneficiaryName && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.beneficiaryName}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="beneficiaryRelation" className="text-base text-foreground">Your Relationship to Beneficiary *</Label>
                    <Select value={formData.beneficiaryRelation} onValueChange={(value) => updateFormData('beneficiaryRelation', value)}>
                      <SelectTrigger className="mt-2 py-6 bg-secondary border-border">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="family">Family Member</SelectItem>
                        <SelectItem value="friend">Friend</SelectItem>
                        <SelectItem value="colleague">Colleague</SelectItem>
                        <SelectItem value="organization">Organization Representative</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Verification Notice */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Identity Verification Required</p>
                    <p className="text-sm text-muted-foreground">
                      Before funds can be withdrawn, we'll need to verify your identity (KYC) to comply with 
                      anti-money laundering regulations. This helps keep our platform safe.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">{formData.type === 'project' ? 'Project Settings' : 'Campaign Settings'}</h2>
              <p className="text-muted-foreground">Configure transparency and trust features for your {formData.type === 'project' ? 'project' : 'campaign'}</p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Escrow & Milestones */}
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-foreground flex items-center">
                  <Shield className="w-5 h-5 text-emerald-400 mr-2" /> Trust & Transparency
                </h3>
                
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="enableEscrow"
                    checked={formData.enableEscrow}
                    onCheckedChange={(checked) => updateFormData('enableEscrow', checked)}
                  />
                  <div>
                    <label htmlFor="enableEscrow" className="font-medium text-foreground cursor-pointer">
                      Enable Escrow Protection
                    </label>
                    <p className="text-sm text-muted-foreground">
                      Funds are held securely and released based on milestone completion
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="enableMilestones"
                    checked={formData.enableMilestones}
                    onCheckedChange={(checked) => updateFormData('enableMilestones', checked)}
                  />
                  <div>
                    <label htmlFor="enableMilestones" className="font-medium text-foreground cursor-pointer">
                      Enable Milestone Funding
                    </label>
                    <p className="text-sm text-muted-foreground">
                      Break your goal into milestones to show donors how funds will be used
                    </p>
                  </div>
                </div>
              </div>

              {/* Milestones */}
              {formData.enableMilestones && (
                <div className="bg-secondary rounded-xl p-6">
                  <h4 className="font-medium text-foreground mb-2">Define Milestones</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Break down your fundraising goal into specific milestones to build trust with donors.
                  </p>
                  
                  {/* Examples */}
                  <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 mb-4 border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">💡 Milestone Examples:</p>
                    <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                      <li>• <strong>Medical:</strong> "Initial Treatment" → "Surgery" → "Rehabilitation"</li>
                      <li>• <strong>Education:</strong> "Tuition Fee" → "Books & Supplies" → "Living Expenses"</li>
                      <li>• <strong>Emergency:</strong> "Immediate Relief" → "Temporary Housing" → "Rebuilding"</li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    {formData.milestones.map((milestone, index) => (
                      <div key={index} className="bg-card rounded-lg p-4 border border-border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-muted-foreground">Milestone {index + 1}</span>
                          {formData.milestones.length > 1 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600 h-6 px-2"
                              onClick={() => {
                                const newMilestones = formData.milestones.filter((_, i) => i !== index);
                                setFormData({ ...formData, milestones: newMilestones });
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="e.g., Initial Treatment Phase"
                            value={milestone.name}
                            onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                          />
                          <div className="relative">
                            <Input
                              type="number"
                              placeholder="5000"
                              min="1"
                              value={milestone.amount}
                              onChange={(e) => {
                                const val = e.target.value;
                                // Only allow positive numbers
                                if (val === '' || (parseFloat(val) >= 0)) {
                                  updateMilestone(index, 'amount', val);
                                }
                              }}
                              onKeyDown={(e) => {
                                // Prevent minus sign
                                if (e.key === '-' || e.key === 'e') {
                                  e.preventDefault();
                                }
                              }}
                              className="pr-16"
                            />
                            {milestone.amount && parseFloat(milestone.amount) <= 0 && (
                              <p className="text-xs text-red-500 mt-1">Amount must be greater than 0</p>
                            )}
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                              {currencies.find(c => c.code === formData.currency)?.symbol || '$'} {formData.currency}
                            </span>
                          </div>
                        </div>
                        <Textarea
                          placeholder="Describe what this milestone will achieve. E.g., 'This will cover the first round of chemotherapy treatment including medication and hospital stay.'"
                          className="mt-3"
                          value={milestone.description}
                          onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                          rows={3}
                        />
                      </div>
                    ))}
                    <Button variant="outline" onClick={addMilestone} className="w-full">
                      + Add Another Milestone
                    </Button>
                    
                    {/* Total Summary */}
                    {formData.milestones.some(m => m.amount) && (
                      <div className="bg-muted/50 rounded-lg p-3 flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Total Milestones Amount:</span>
                        <span className="font-semibold">
                          {currencies.find(c => c.code === formData.currency)?.symbol || '$'}
                          {formData.milestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0).toLocaleString()}
                          {' '}{formData.currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Donor Settings */}
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-foreground flex items-center">
                  <Eye className="w-5 h-5 text-teal-400 mr-2" /> Donor Settings
                </h3>
                
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="allowAnonymous"
                    checked={formData.allowAnonymousDonations}
                    onCheckedChange={(checked) => updateFormData('allowAnonymousDonations', checked)}
                  />
                  <div>
                    <label htmlFor="allowAnonymous" className="font-medium text-foreground cursor-pointer">
                      Allow Anonymous Donations
                    </label>
                    <p className="text-sm text-muted-foreground">Let donors choose to hide their name</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="showDonorNames"
                    checked={formData.showDonorNames}
                    onCheckedChange={(checked) => updateFormData('showDonorNames', checked)}
                  />
                  <div>
                    <label htmlFor="showDonorNames" className="font-medium text-foreground cursor-pointer">
                      Show Donor Names Publicly
                    </label>
                    <p className="text-sm text-muted-foreground">Display donor names on your campaign page</p>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) => updateFormData('agreeToTerms', checked)}
                  />
                  <label htmlFor="agreeToTerms" className="text-sm text-muted-foreground cursor-pointer">
                    I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Terms of Service</a> and 
                    confirm that my campaign complies with all platform guidelines.
                  </label>
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agreeToPolicy"
                    checked={formData.agreeToPolicy}
                    onCheckedChange={(checked) => updateFormData('agreeToPolicy', checked)}
                  />
                  <label htmlFor="agreeToPolicy" className="text-sm text-muted-foreground cursor-pointer">
                    I understand the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Privacy Policy</a> and 
                    how my information will be used.
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    return canProceed();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Pending Campaign Restored Notice - Only for logged-in users */}
        {user && localStorage.getItem('pendingCampaign') && (
          <div className="bg-emerald-500 text-white py-3 px-4">
            <div className="max-w-4xl mx-auto flex justify-between items-center">
              <p className="font-medium">
                ✓ Your previous campaign data has been restored! 
                {formData.imageWasRemoved && ' (Please re-upload your cover image)'}
              </p>
              <button 
                onClick={() => {
                  localStorage.removeItem('pendingCampaign');
                  localStorage.removeItem('pendingCampaignStep');
                  setFormData({
                    category: '',
                    title: '',
                    goal: '',
                    currency: 'USD',
                    duration: '30',
                    location: '',
                    country: 'US',
                    story: '',
                    coverImage: null,
                    additionalImages: [],
                    videoUrl: '',
                    beneficiaryType: '',
                    beneficiaryName: '',
                    beneficiaryRelation: '',
                    enableEscrow: true,
                    enableMilestones: true,
                    milestones: [{ name: '', amount: '', description: '' }],
                    allowAnonymousDonations: true,
                    showDonorNames: true,
                    agreeToTerms: false,
                    agreeToPolicy: false,
                  });
                  setCurrentStep(1);
                  updateStepInUrl(1);
                }}
                className="text-sm underline hover:no-underline"
              >
                Start Fresh Instead
              </button>
            </div>
          </div>
        )}
        
        {/* Progress Bar */}
        <div className="bg-card border-b border-border sticky top-16 z-40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Step {currentStep} of {totalSteps}</span>
              <div className="flex items-center gap-3">
                {user && autoSaveStatus === 'saved' && (
                  <span className="text-xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Auto-saved
                  </span>
                )}
                <span className="text-sm text-muted-foreground">{Math.round((currentStep / totalSteps) * 100)}% complete</span>
              </div>
            </div>
            <Progress value={(currentStep / totalSteps) * 100} className="h-2" />
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="bg-card border-t border-border sticky bottom-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={prevStep}
                  className="text-teal-500 hover:text-teal-400 hover:bg-teal-500/20 font-medium"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> {currentStep === 1 ? 'Home' : 'Back'}
                </Button>
              </div>

              {/* Save Draft & Navigation Buttons */}
              <div className="flex items-center gap-3">
                {/* Save Draft Button - Always visible after step 1 */}
                {currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!user) {
                        // Save current form data to localStorage temporarily and redirect to login
                        localStorage.setItem('pendingCampaign', JSON.stringify(formData));
                        localStorage.setItem('pendingCampaignStep', currentStep.toString());
                        localStorage.setItem('redirectAfterLogin', '/create');
                        navigate('/login?redirect=/create&reason=save_draft');
                      } else {
                        saveDraft();
                      }
                    }}
                    disabled={savingDraft}
                    className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                  >
                    {savingDraft ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                      </>
                    ) : draftSaved ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" /> Saved!
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" /> Save Draft
                      </>
                    )}
                  </Button>
                )}

                {/* Navigation buttons - Hide Continue on Step 1 (users click category to proceed) */}
                {currentStep > 1 && currentStep < totalSteps ? (
                  <Button
                    onClick={nextStep}
                    className={`bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 ${!isStepValid() ? 'opacity-70' : ''}`}
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : currentStep === totalSteps ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={!isStepValid() || isSubmitting}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  >
                    {isSubmitting ? 'Creating...' : `Launch ${formData.type === 'project' ? 'Project' : 'Campaign'}`} <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : null}
              </div>
              {error && <p className="text-red-500 text-sm ml-4">{error}</p>}
            </div>
          </div>
        </div>
      </main>

      {/* AI Image Generator Modal */}
      <ImageGeneratorModal
        isOpen={showImageGenerator}
        onClose={() => setShowImageGenerator(false)}
        initialTitle={formData.title}
        initialCategory={formData.category}
        onImageGenerated={(imageBase64) => {
          // Convert base64 to blob and upload
          fetch(imageBase64)
            .then(res => res.blob())
            .then(async (blob) => {
              const formDataUpload = new FormData();
              formDataUpload.append('file', blob, 'ai-generated.png');
              
              try {
                const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
                  method: 'POST',
                  body: formDataUpload
                });
                
                const result = await response.json();
                if (result.success) {
                  const imageUrl = `${BACKEND_URL}${result.url}`;
                  updateFormData('coverImage', imageUrl);
                } else {
                  // Fallback to base64
                  updateFormData('coverImage', imageBase64);
                }
              } catch (err) {
                // Fallback to base64
                updateFormData('coverImage', imageBase64);
              }
            });
        }}
      />
    </div>
  );
};

export default CreateCampaignPage;
